import React from "react";
import { Tabs, Button, message } from "antd";

// These field names are read by KlaviyoInboundNormalizer on the backend. A typo here
// means the webhook authenticates, returns 200, and the event is silently dropped.
// occurred_at is deliberately omitted — the normalizer falls back to the server clock
// when it is absent, and a property-change flow may have no event variable to fill it.
const ATTRIBUTE_TEMPLATE = `{
  "event_type": "attribute.changed",
  "profile_id": "{{ person.KlaviyoID|default:'' }}",
  "properties": {
    "your_klaviyo_property_name": "{{ person|lookup:'your_klaviyo_property_name' }}"
  }
}`;

// properties.product is required -- InboundEventProcessor.processPurchaseEvent reads it
// (falling back to "Unknown Product" otherwise) to find-or-create the purchased product and
// associate it with the contact. order_id is not read by anything; it's kept only as an
// informational field for the client's own reference.
const PURCHASE_TEMPLATE = `{
  "event_type": "commerce.purchase",
  "profile_id": "{{ person.KlaviyoID|default:'' }}",
  "properties": {
    "product": "{{ event.ItemNames.0|default:'' }}",
    "order_id": "{{ event.OrderId|default:'' }}"
  }
}`;

function handleCopy(text) {
  navigator.clipboard
    .writeText(text)
    .then(() => message.success("Copied"))
    .catch(() => message.error("Failed to copy"));
}

function TemplatePane({ testId, template, guidance }) {
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <Button onClick={() => handleCopy(template)}>Copy</Button>
      </div>
      <pre
        data-testid={testId}
        style={{
          background: "#f6f8fa",
          border: "1px solid #e2e8f0",
          borderRadius: 6,
          padding: 16,
          fontFamily: "monospace",
          fontSize: 13,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {template}
      </pre>
      <p style={{ fontSize: 13, color: "#555", lineHeight: 1.6, marginTop: 12 }}>
        {guidance}
      </p>
    </div>
  );
}

export default function WebhookBodyTemplates() {
  const tabItems = [
    {
      key: "attribute",
      label: "Attribute Change",
      // Both panes must be in the DOM (not just the active one) so a client copying
      // from the inactive tab — or a test reading it — finds the content there.
      forceRender: true,
      children: (
        <TemplatePane
          testId="template-attribute"
          template={ATTRIBUTE_TEMPLATE}
          guidance={
            <>
              Replace <code>your_klaviyo_property_name</code> — in both places it
              appears — with the Vendor Field from your INBOUND attribute mapping (the
              Klaviyo property name, shown when you created the mapping). The second
              occurrence is the Klaviyo variable that carries that property's value —
              one line per mapped attribute. Leave every field outside{" "}
              <code>properties</code> exactly as written.
            </>
          }
        />
      ),
    },
    {
      key: "purchase",
      label: "Purchase",
      forceRender: true,
      children: (
        <TemplatePane
          testId="template-purchase"
          template={PURCHASE_TEMPLATE}
          guidance={
            <>
              Replace <code>product</code>'s value with the Klaviyo variable for the
              purchased product's name on this flow's trigger event — this is required.
              You may also set <code>order_id</code>'s value to the order identifier as an
              informational field. Leave every field outside <code>properties</code>{" "}
              exactly as written.
            </>
          }
        />
      ),
    },
  ];

  return <Tabs items={tabItems} destroyInactiveTabPane={false} />;
}
